// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Breakers on the drawing: the geometry of the drawout-breaker glyph, where
 * each breaker sits on its conductor, and how a conductor's breakers roll up
 * into one drawn state.
 *
 * Pure, like the rest of `utils/powerFlow*`: the edge layer, the legend, the
 * dev renderer and the tests all read the same functions, so the glyph and
 * the cut in the run beneath it agree to the pixel. The React glyph itself is
 * `pages/…/conductors/BreakerGlyph.tsx`, which only draws.
 */

import type { BreakerState } from './powerFlowLive';
import type { ConductorAnchor, PowerFlowConductor } from './powerFlowRouting';

/*
 * Proportions transcribed from the SGI-A engineering sheet, in units of the
 * contact arc's radius r: the dots sit 2r apart; each drawout chevron is
 * 1.15r either side of the run and 0.9r deep; the two chevrons of a pair nest
 * 0.9r apart; and there is 1r of bare run between the inner chevron and its
 * contact dot. Ours at r = 6.
 */
/** Radius of the contact arc; the two contact dots sit at ±BREAKER_ARC_R. */
export const BREAKER_ARC_R = 6;
export const CHEVRON_HALF_WIDTH = 7;
export const CHEVRON_DEPTH = 5.5;
const CONTACT_CLEARANCE = 6;
/** Where the inner chevron's arms meet the run, from the glyph centre. */
export const INNER_ARMS = BREAKER_ARC_R + CONTACT_CLEARANCE;
/** Half-height of the glyph, stage px: the outer chevron tips sit at ±BREAKER_REACH. */
export const BREAKER_REACH = INNER_ARMS + 2 * CHEVRON_DEPTH;
/**
 * Padding between a breaker and the switchgear it belongs to, beyond the
 * bus's own text bands. The SGI-A sheet draws every breaker inside its board
 * with clear space to the bar; this is that space.
 */
export const BREAKER_BUS_PADDING = 12;
/** Below a bar: the band the bus sub-label (`Mechanical load`) prints in. */
export const BUS_SUBLABEL_BAND = 18;
/** Above a bar: the label strip (`SWGR-1-A`, meter, badges); `busLabelHeight`. */
export const BUS_LABEL_BAND = 20;
/** Clearance kept from a non-bus end: a symbol's lead, a turn. */
const BREAKER_END_CLEARANCE = 8;
/**
 * Vertical run a breaker needs on a feeder out of or into a bus: the bus's
 * text band, the padding, the glyph, and clearance at the far end. The model
 * widens the rank gap to at least this on a site with breakers.
 */
export const BREAKER_RUN_NEEDED =
  Math.max(BUS_SUBLABEL_BAND, BUS_LABEL_BAND) +
  BREAKER_BUS_PADDING +
  2 * BREAKER_REACH +
  BREAKER_END_CLEARANCE;
/** Width reserved beside a breaker for its alarm mark: the triangle and the word. */
export const ALARM_MARK_WIDTH = 52;
/** How far an open breaker's arc swings off its bottom contact, degrees. */
export const OPEN_SWING_DEG = 38;

/**
 * The state the mark draws, rolled up over every breaker on the conductor.
 *
 * Open when any breaker on the run is open — one open device opens the run —
 * closed only when every one is closed, unknown otherwise; in alarm when any
 * is. Exported so the roll-up is testable without rendering.
 */
export const breakerMarkState = (
  via: readonly string[],
  breakerOf: Map<string, BreakerState> | undefined
): { position: BreakerState['position']; alarm: boolean } => {
  const states = via.map(id => breakerOf?.get(id));
  const position: BreakerState['position'] = states.some(s => s?.position === 'open')
    ? 'open'
    : states.length > 0 && states.every(s => s?.position === 'closed')
      ? 'closed'
      : 'unknown';
  return { position, alarm: states.some(s => s?.alarm === true) };
};

/** How a position reads in a title or caption. */
export const POSITION_WORD: Record<BreakerState['position'], string> = {
  closed: 'closed',
  open: 'open',
  unknown: 'position unknown',
};

/**
 * What a breaker mark says of itself — its hover title and its entry in the
 * accessible breaker list, so the two never disagree.
 */
export const breakerTitle = (
  via: readonly string[],
  breakerOf: Map<string, BreakerState> | undefined,
  labelOf?: (_id: string) => string
): string => {
  const { position, alarm } = breakerMarkState(via, breakerOf);
  const names = via.map(id => labelOf?.(id) ?? id).join(', ');
  return `${names} · ${POSITION_WORD[position]}${alarm ? ' · alarm' : ''}`;
};

/** Where a breaker is drawn: its centre, and whether it lies along a horizontal run. */
export interface BreakerPlacement extends ConductorAnchor {
  horizontal?: boolean;
}

/**
 * Where the conductor's flow dash enters the glyph at `at`: the path distance
 * from the conductor's start to the contact the run reaches first, and whether
 * the run passes through the glyph against the arc's own drawing direction
 * (top to bottom; right to left once turned horizontal). The glyph offsets
 * its dash by the distance and reverses its arc when told, so the dash runs
 * through the breaker without a jump. `undefined` when the placement is not
 * on the conductor's polyline — the fallback anchor — and the dash is left
 * unphased.
 */
export const breakerFlowEntry = (
  points: readonly ConductorAnchor[],
  at: BreakerPlacement
): { distance: number; reversed: boolean } | undefined => {
  let travelled = 0;
  for (let index = 0; index + 1 < points.length; index += 1) {
    const a = points[index];
    const b = points[index + 1];
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    const onSegment =
      Math.abs((at.x - a.x) * (b.y - a.y) - (at.y - a.y) * (b.x - a.x)) <= 0.5 * length &&
      at.x >= Math.min(a.x, b.x) - 0.5 &&
      at.x <= Math.max(a.x, b.x) + 0.5 &&
      at.y >= Math.min(a.y, b.y) - 0.5 &&
      at.y <= Math.max(a.y, b.y) + 0.5;
    if (length > 0 && onSegment) {
      const toCentre = Math.hypot(at.x - a.x, at.y - a.y);
      const reversed = at.horizontal ? b.x > a.x : b.y < a.y;
      return { distance: travelled + toCentre - BREAKER_ARC_R, reversed };
    }
    travelled += length;
  }
  return undefined;
};

/** An axis-aligned box something else already occupies. */
export interface PlacementObstacle {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

interface Run {
  horizontal: boolean;
  /** Fixed coordinate: x of a vertical run, y of a horizontal one. */
  at: number;
  /** Extent along the run, low to high. */
  from: number;
  to: number;
  /** Path order: which end the conductor leaves from. */
  forward: boolean;
}

const runsOf = (points: readonly ConductorAnchor[]): Run[] => {
  const runs: Run[] = [];
  for (let index = 0; index + 1 < points.length; index += 1) {
    const a = points[index];
    const b = points[index + 1];
    if (Math.abs(a.x - b.x) <= 0.5 && a.y !== b.y) {
      runs.push({
        horizontal: false,
        at: a.x,
        from: Math.min(a.y, b.y),
        to: Math.max(a.y, b.y),
        forward: b.y > a.y,
      });
    } else if (Math.abs(a.y - b.y) <= 0.5 && a.x !== b.x) {
      runs.push({
        horizontal: true,
        at: a.y,
        from: Math.min(a.x, b.x),
        to: Math.max(a.x, b.x),
        forward: b.x > a.x,
      });
    }
  }
  return runs;
};

/** The box a breaker occupies at `p`, alarm included, with a small margin. */
export const breakerBox = (p: BreakerPlacement): PlacementObstacle =>
  p.horizontal
    ? {
        x0: p.x - BREAKER_REACH - 2,
        y0: p.y - CHEVRON_HALF_WIDTH - 2,
        x1: p.x + BREAKER_ARC_R + ALARM_MARK_WIDTH,
        y1: p.y + BREAKER_ARC_R + 20,
      }
    : {
        x0: p.x - CHEVRON_HALF_WIDTH - 2,
        y0: p.y - BREAKER_REACH - 2,
        x1: p.x + BREAKER_ARC_R + ALARM_MARK_WIDTH,
        y1: p.y + BREAKER_REACH + 2,
      };

const overlaps = (a: PlacementObstacle, b: PlacementObstacle) =>
  a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;

/** Along-run clearance a horizontal breaker keeps from the turns at either end. */
const TURN_CLEARANCE = 10;
/** Step when sliding a horizontal breaker clear of what crosses its run. */
const SLIDE_STEP = 4;

/**
 * Where the breaker sits on its conductor. In order:
 *
 * 1. At the **bus end** — the board the breaker was folded into — as a
 *    one-line draws it: below the bar on a feeder, past the sub-label band and
 *    `BREAKER_BUS_PADDING`; above the bar on an incomer, past the label strip
 *    and the same padding.
 * 2. On any other vertical run that holds the glyph with clearance at both
 *    ends (the bus bands where a run meets a bus).
 * 3. Along a **horizontal run**, turned a quarter, like the sheet's bus-tie
 *    breaker — for a run that leaves its bus and turns almost at once, where a
 *    vertical glyph could only sit on the bar. It slides from the conductor's
 *    bus end along the run until nothing in `obstacles` crosses it.
 * 4. Failing all of that, the middle of the longest run; failing that, the
 *    anchor.
 *
 * `end: 'source'` skips step 1 for the conductor's own first run out of its
 * source (see `placeBreakers`).
 *
 * Pure and exported: the edge layer uses it for both the glyph and the cut in
 * the run beneath it, and they must agree to the pixel.
 */
export const breakerPlacement = (
  conductor: PowerFlowConductor,
  {
    end = 'bus',
    obstacles = [],
  }: { end?: 'bus' | 'source'; obstacles?: readonly PlacementObstacle[] } = {}
): BreakerPlacement => {
  const runs = runsOf(conductor.points);
  if (runs.length === 0) return conductor.anchor;
  const vertical = runs.filter(run => !run.horizontal);
  const first = runs[0];
  const last = runs[runs.length - 1];
  const { busEnd } = conductor;
  const clear = (p: BreakerPlacement) => !obstacles.some(o => overlaps(breakerBox(p), o));

  if (end === 'source') {
    // The conductor's own first run, which no other conductor shares: hug the
    // source if the glyph fits with clearance, else centre on it — anywhere
    // else would be back on the run it is being moved off.
    if (!first.horizontal) {
      const y = first.from + BREAKER_END_CLEARANCE + BREAKER_REACH;
      return y + BREAKER_REACH + BREAKER_END_CLEARANCE <= first.to
        ? { x: first.at, y }
        : { x: first.at, y: (first.from + first.to) / 2 };
    }
  } else {
    const atSource = (): BreakerPlacement | undefined => {
      if (first.horizontal) return undefined;
      const y = first.from + BUS_SUBLABEL_BAND + BREAKER_BUS_PADDING + BREAKER_REACH;
      return y + BREAKER_REACH + BREAKER_END_CLEARANCE <= first.to ? { x: first.at, y } : undefined;
    };
    const atTarget = (): BreakerPlacement | undefined => {
      if (last.horizontal) return undefined;
      const y = last.to - BUS_LABEL_BAND - BREAKER_BUS_PADDING - BREAKER_REACH;
      return y - BREAKER_REACH - BREAKER_END_CLEARANCE >= last.from ? { x: last.at, y } : undefined;
    };
    // Bar to bar (a bypass), above the far board with that board's incomers:
    // the UPS output breakers it parallels sit there. Below the source bar
    // only when the far end has no room.
    const atBus = conductor.busToBus
      ? (atTarget() ?? atSource())
      : busEnd === 'source'
        ? atSource()
        : busEnd === 'target'
          ? atTarget()
          : undefined;
    if (atBus) return atBus;
  }

  // 2. Any vertical run that fits, with the bus bands where it meets a bus.
  for (const run of vertical) {
    const topBand =
      run === first && busEnd === 'source'
        ? BUS_SUBLABEL_BAND + BREAKER_BUS_PADDING
        : BREAKER_END_CLEARANCE;
    const bottomBand =
      run === last && busEnd === 'target'
        ? BUS_LABEL_BAND + BREAKER_BUS_PADDING
        : BREAKER_END_CLEARANCE;
    const y = run.from + topBand + BREAKER_REACH;
    if (y + BREAKER_REACH + bottomBand <= run.to && clear({ x: run.at, y }))
      return { x: run.at, y };
  }

  // 3. Along a horizontal run, slid clear of whatever crosses it.
  const horizontals = runs.filter(run => run.horizontal);
  if (busEnd === 'target') horizontals.reverse();
  for (const run of horizontals) {
    const low = run.from + TURN_CLEARANCE + BREAKER_REACH;
    const high = run.to - TURN_CLEARANCE - BREAKER_REACH;
    if (high < low) continue;
    // From the end nearest the bus the breaker belongs to.
    const fromLow = busEnd === 'target' ? !run.forward : run.forward;
    for (let offset = 0; low + offset <= high; offset += SLIDE_STEP) {
      const x = fromLow ? low + offset : high - offset;
      const p = { x, y: run.at, horizontal: true };
      if (clear(p)) return p;
    }
  }

  // 4. Last resort.
  const longest = runs.reduce((best, run) =>
    run.to - run.from > best.to - best.from ? run : best
  );
  const mid = (longest.from + longest.to) / 2;
  return longest.horizontal
    ? { x: mid, y: longest.at, horizontal: true }
    : { x: longest.at, y: mid };
};

/** Every straight piece of a conductor as a thin box, for placement to avoid. */
const conductorObstacles = (conductor: PowerFlowConductor): PlacementObstacle[] =>
  runsOf(conductor.points).map(run =>
    run.horizontal
      ? { x0: run.from, y0: run.at - 1, x1: run.to, y1: run.at + 1 }
      : { x0: run.at - 1, y0: run.from, x1: run.at + 1, y1: run.to }
  );

/**
 * Placements for every breaker-carrying conductor, keyed by conductor id.
 *
 * Each goes where `breakerPlacement` puts it, clear of every other conductor
 * and of the breakers already placed. Where two would land on the same spot —
 * incomers the router brings into a bar through one shared drop, two UPS
 * outputs entering the end of an output board — they move to their own run
 * out of the source, where each conductor is still alone.
 */
export const placeBreakers = (
  conductors: readonly PowerFlowConductor[]
): Map<string, BreakerPlacement> => {
  const carrying = conductors.filter(conductor => (conductor.via?.length ?? 0) > 0);
  const runsById = new Map(
    conductors.map(conductor => [conductor.id, conductorObstacles(conductor)])
  );
  const othersOf = (id: string) =>
    conductors.filter(other => other.id !== id).flatMap(other => runsById.get(other.id) ?? []);

  const atBus = new Map(
    carrying.map(conductor => [
      conductor.id,
      breakerPlacement(conductor, { obstacles: othersOf(conductor.id) }),
    ])
  );
  const collides = (a: BreakerPlacement, b: BreakerPlacement) =>
    Math.abs(a.x - b.x) < 1 && Math.abs(a.y - b.y) < 2 * BREAKER_REACH;
  // Collisions are judged against the first-pass placements, all of them,
  // before any moves: moving one first would hide the collision from its
  // partner.
  const placed = new Map<string, BreakerPlacement>();
  for (const conductor of carrying) {
    const at = atBus.get(conductor.id) as BreakerPlacement;
    const shared = carrying.some(
      other => other.id !== conductor.id && collides(at, atBus.get(other.id) as BreakerPlacement)
    );
    placed.set(
      conductor.id,
      shared
        ? breakerPlacement(conductor, {
            end: 'source',
            obstacles: [...othersOf(conductor.id), ...Array.from(placed.values()).map(breakerBox)],
          })
        : at
    );
  }
  return placed;
};
